import { Component, effect } from '@angular/core';
import { SettingDTO } from '../../../services/api/api';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { WebSettingsService } from '../../../services/client/web-settings.service';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'page-web-settings',
  templateUrl: './web-settings.component.html',
  styleUrls: ['./web-settings.component.scss'],
  standalone: false,
})
export class WebSettingsComponent {
  public loading: boolean;
  public settingsForm: FormGroup;
  public webSettings: Record<string, SettingDTO>;

  constructor(
    private readonly toaster: ToasterService,
    private readonly translate: TranslateService,
    private readonly webSettingsService: WebSettingsService
  ) {
    this.loading = true;
    this.webSettings = {};
    this.settingsForm = new FormGroup({});
    effect(() => {
      this.webSettings = this.webSettingsService.settings();
      this.loading = true;

      const controls = {};
      for (const key of Object.keys(this.webSettings)) {
        controls[key] = new FormControl(this.webSettings[key].value);
      }
      this.settingsForm = new FormGroup(controls);

      this.loading = false;
    });
  }

  public async save() {
    // check for changes
    this.loading = true;
    const changedKeys = this.getChangedSettingKeys();
    const results = await Promise.allSettled(
      changedKeys.map(key => this.webSettingsService.setSetting(key, this.getFormControl(key).value))
    );

    const failedKeys = changedKeys.filter((_, i) => results[i].status === 'rejected');
    const failedSettingsString = failedKeys.map(key => this.translate.instant('lang.clientManagement.settings.setting.' + key)).join(', ');

    if (failedKeys.length === 0) {
      this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.settings.saved'));
    } else {
      console.error('Failed settings:', failedKeys);
      this.toaster.toast(
        ToastType.WARNING,
        this.translate.instant('lang.clientManagement.settings.partialFailure', { failed: failedSettingsString })
      );
    }
    this.loading = false;
  }

  public reset(): void {
    // reset settings
    const promises = this.formControlKeys
      .filter(key => this.webSettings[key].override)
      .map(async filteredKey => {
        try {
          const newValue = await this.webSettingsService.deleteSetting(filteredKey);
          this.getFormControl(filteredKey).setValue(newValue);
        } catch (error) {
          console.error(`Failed to reset setting for key: ${filteredKey}`, error);
          throw error; // Ensure errors are captured in Promise.all
        }
      });

    Promise.all(promises)
      .then(() => {
        this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.settings.resetDone'));
      })
      .catch(() => {
        this.toaster.toast(ToastType.ERROR, this.translate.instant('lang.clientManagement.settings.resetFailed'));
      });
  }

  get formControlKeys(): string[] {
    if (this.settingsForm) {
      return Object.keys(this.settingsForm.controls);
    }
    return [];
  }

  public getFormControl(key: string): FormControl {
    return this.settingsForm.get(key) as FormControl;
  }

  private hasSettingChanged(key: string): boolean {
    const control = this.getFormControl(key);
    return control.touched && control.value !== this.webSettings[key].value;
  }

  private getChangedSettingKeys(): string[] {
    const keys = [];
    for (const key of this.formControlKeys) {
      if (this.hasSettingChanged(key)) {
        keys.push(key);
      }
    }
    return keys;
  }
}
